import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Autocomplete, Box, CircularProgress, TextField } from '@mui/material';
import {
  useGetTransportDrivers,
  type TransportDriver,
} from 'api/transportDriver/transportDriver';

type DriverField = 'driverName' | 'driverDocumentNumber' | 'driverPhone';

const FIELD_TO_DRIVER_VALUE: Record<
  DriverField,
  (driver: TransportDriver) => string
> = {
  driverName: (driver) => driver.fullName,
  driverDocumentNumber: (driver) => driver.documentNumber,
  driverPhone: (driver) => driver.phone,
};

interface DriverOption {
  label: string;
  disambiguator?: string;
  driver: TransportDriver;
}

interface EditableDriverFieldInputProps {
  field: DriverField;
  value: any;
  onChange: (value: any) => void;
  onSave: (value: any, skipDriverCheck?: boolean) => void;
  onCancel: () => void;
  onRowFieldSave?: (
    columnKey: string,
    value: any,
    skipDriverCheck?: boolean,
  ) => void;
  hasValidationError?: boolean;
}

export const EditableDriverFieldInput: React.FC<
  EditableDriverFieldInputProps
> = ({
  field,
  value,
  onSave,
  onCancel,
  onRowFieldSave,
  hasValidationError = false,
}) => {
  const [inputValue, setInputValue] = useState(value ?? '');
  const [open, setOpen] = useState(false);
  const [accumulatedDrivers, setAccumulatedDrivers] = useState<
    TransportDriver[]
  >([]);
  const inputValueRef = useRef(value ?? '');
  const hasSavedRef = useRef(false);
  const containerRef = useRef<HTMLDivElement>(null);
  const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const pageNumberRef = useRef(1);
  const filterTextRef = useRef('');
  const hasAutoOpenedRef = useRef(false);
  const isFetchingNextPageRef = useRef(false);

  const {
    data: drivers,
    loading,
    getTransportDrivers,
  } = useGetTransportDrivers();
  const getFieldValue = FIELD_TO_DRIVER_VALUE[field];

  useEffect(() => {
    getTransportDrivers({ filterText: '', resultSize: 25, pageNumber: 1 });
  }, [getTransportDrivers]);

  useEffect(
    () => () => {
      if (debounceRef.current) clearTimeout(debounceRef.current);
    },
    [],
  );

  useEffect(() => {
    if (!drivers) return;
    isFetchingNextPageRef.current = false;
    setAccumulatedDrivers((prev) => {
      if (pageNumberRef.current === 1) return drivers;
      const map = new Map(prev.map((driver) => [driver.id, driver]));
      drivers.forEach((driver) => map.set(driver.id, driver));
      return Array.from(map.values());
    });
  }, [drivers]);

  // NOTE: open the dropdown as soon as the first page of drivers finishes loading
  useEffect(() => {
    if (!loading && !hasAutoOpenedRef.current) {
      hasAutoOpenedRef.current = true;
      setOpen(true);
    }
  }, [loading]);

  const fetchNextPage = useCallback(() => {
    if (isFetchingNextPageRef.current || loading) return;
    isFetchingNextPageRef.current = true;
    pageNumberRef.current += 1;
    getTransportDrivers({
      filterText: filterTextRef.current,
      resultSize: 25,
      pageNumber: pageNumberRef.current,
    });
  }, [getTransportDrivers, loading]);

  const handleListboxScroll = useCallback(
    (event: React.UIEvent<HTMLUListElement>) => {
      const target = event.currentTarget;
      const buffer = 10;
      if (
        target.scrollTop + target.clientHeight >=
        target.scrollHeight - buffer
      ) {
        fetchNextPage();
      }
    },
    [fetchNextPage],
  );

  const options: DriverOption[] = (() => {
    const driversWithValue = accumulatedDrivers.filter(
      (driver) => !!getFieldValue(driver),
    );

    const seen = new Set<string>();
    const result: DriverOption[] = [];
    driversWithValue.forEach((driver) => {
      const val = getFieldValue(driver);
      const disambiguator =
        field === 'driverName' ? driver.documentNumber : driver.fullName;
      const key = `${val}__${disambiguator}`;
      if (seen.has(key)) return;
      seen.add(key);
      result.push({ label: val, disambiguator, driver });
    });
    return result;
  })();

  const handleChange = useCallback(
    (_: React.SyntheticEvent, newValue: DriverOption | string | null) => {
      const isOption = newValue !== null && typeof newValue === 'object';
      const val = (isOption ? newValue.label : newValue) ?? '';
      setInputValue(val);
      inputValueRef.current = val;
      hasSavedRef.current = true;

      const matchingDriver = isOption
        ? newValue.driver
        : accumulatedDrivers.find(
            (driver) => FIELD_TO_DRIVER_VALUE[field](driver) === val,
          );

      if (matchingDriver && onRowFieldSave) {
        const allThree: Record<DriverField, string> = {
          driverName: FIELD_TO_DRIVER_VALUE.driverName(matchingDriver),
          driverDocumentNumber:
            FIELD_TO_DRIVER_VALUE.driverDocumentNumber(matchingDriver),
          driverPhone: FIELD_TO_DRIVER_VALUE.driverPhone(matchingDriver),
        };
        (Object.keys(allThree) as DriverField[]).forEach((key) => {
          if (key === field) {
            onSave(allThree[key], true);
          } else {
            onRowFieldSave(key, allThree[key], true);
          }
        });
      } else {
        onSave(val);
      }
    },
    [field, accumulatedDrivers, onSave, onRowFieldSave],
  );

  const handleInputChange = useCallback(
    (_: React.SyntheticEvent, newValue: string, reason: string) => {
      if (reason !== 'input') return;
      setInputValue(newValue);
      inputValueRef.current = newValue;
      if (debounceRef.current) clearTimeout(debounceRef.current);
      debounceRef.current = setTimeout(() => {
        filterTextRef.current = newValue;
        pageNumberRef.current = 1;
        getTransportDrivers({
          filterText: newValue,
          resultSize: 25,
          pageNumber: 1,
        });
      }, 300);
    },
    [getTransportDrivers],
  );

  const handleKeyDown = (event: React.KeyboardEvent) => {
    if (event.key === 'Escape') {
      onCancel();
    } else if (event.key === 'Enter') {
      hasSavedRef.current = true;
      onSave(inputValueRef.current);
    }
  };

  const handleBlur = useCallback(() => {
    setTimeout(() => {
      if (hasSavedRef.current) {
        hasSavedRef.current = false;
        return;
      }
      const currentFocus = document.activeElement;
      if (
        !containerRef.current ||
        !containerRef.current.contains(currentFocus)
      ) {
        onSave(inputValueRef.current);
      }
    }, 200);
  }, [onSave]);

  const handleClose = useCallback(() => {
    setOpen(false);
  }, []);

  return (
    <div ref={containerRef} onKeyDown={handleKeyDown} style={{ width: '100%' }}>
      <Box
        sx={{
          minWidth: 'auto',
          width: '100%',
          '& .MuiOutlinedInput-root': {
            height: '24px',
            minHeight: '24px',
            padding: '0px 8px',
            fontSize: '0.7rem',
          },
          '& .MuiInputBase-input': {
            padding: '0px',
            height: '24px',
            lineHeight: '1.5em',
            fontSize: '0.7rem',
          },
          '& .MuiAutocomplete-endAdornment': {
            top: 'calc(50% - 10px)',
            ...(hasValidationError && { right: '26px !important' }),
          },
          '& .MuiAutocomplete-clearIndicator': {
            padding: '1px',
            '& .MuiSvgIcon-root': { fontSize: '14px' },
          },
          '& .MuiAutocomplete-popupIndicator': {
            padding: '1px',
            '& .MuiSvgIcon-root': { fontSize: '14px' },
          },
          '& .MuiCircularProgress-root': {
            width: '12px !important',
            height: '12px !important',
          },
        }}
      >
        <Autocomplete<DriverOption, false, false, true>
          freeSolo
          open={open}
          onOpen={() => setOpen(true)}
          onClose={handleClose}
          onBlur={handleBlur}
          value={inputValue}
          inputValue={inputValue}
          onChange={handleChange}
          onInputChange={handleInputChange}
          options={options}
          getOptionLabel={(option) =>
            typeof option === 'string' ? option : option.label
          }
          isOptionEqualToValue={(option, val) =>
            option.label === (typeof val === 'string' ? val : val.label)
          }
          filterOptions={(x) => x}
          loading={loading}
          size='small'
          ListboxProps={{ onScroll: handleListboxScroll }}
          componentsProps={{
            paper: { sx: { width: 'max-content', minWidth: '100%' } },
          }}
          renderOption={(props, option) => (
            <Box
              component='li'
              {...props}
              key={`${option.driver.id}-${option.label}`}
              sx={{ whiteSpace: 'nowrap' }}
            >
              {option.label}
              {option.disambiguator && (
                <Box
                  component='span'
                  sx={{
                    ml: 0.5,
                    fontSize: '0.75em',
                    color: 'text.disabled',
                  }}
                >
                  ({option.disambiguator})
                </Box>
              )}
            </Box>
          )}
          renderInput={(params) => (
            <TextField
              {...params}
              autoFocus
              error={hasValidationError}
              variant='outlined'
              size='small'
              InputProps={{
                ...params.InputProps,
                endAdornment: (
                  <>
                    {loading && <CircularProgress color='inherit' size={10} />}
                    {params.InputProps.endAdornment}
                  </>
                ),
              }}
            />
          )}
        />
      </Box>
    </div>
  );
};
